import { useState, useEffect, useRef, useCallback } from "react";
import entitylogo from "../../assets/images/entitylogo.png";
import {
  login,
  createPin,
  validatePin,
  getLightDarkMode,
} from "../../api/auth";
import { useNavigate } from "react-router-dom";
import CountdownTimer from "../Universal/CountdownTimer";
import { Input } from "../Universal/Input";
import { Button } from "../Universal/Button";
import { useAuth } from "../contexts/AuthContext";
import { useUserContext } from "../../context/UserContext";
import { useTheme } from "../contexts/ThemeContext";

const DEFAULT_CREDENTIALS = {
  email:
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_DEFAULT_USERNAME
      : "",
  password:
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_DEFAULT_PASSWORD
      : "",
};

const ANIMATION_STYLE = `
  :root {
    --panel-dur: 1200ms;
    --panel-ease: cubic-bezier(.16, 1, .3, 1);
    --panel-delay: 120ms;
    --exit-dur: 350ms;
    --exit-ease: cubic-bezier(.4, 0, .2, 1);
  }
  @keyframes panelIn {
    from { opacity: 0; transform: translate3d(0, 36px, 0) scale(.96); }
    to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
  }
  @keyframes panelOut {
    from { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    to   { opacity: 0; transform: translate3d(0, -24px, 0) scale(1.02); }
  }
  @keyframes bgFadeOut {
    from { opacity: 1; } to { opacity: 0; }
  }
  @keyframes bgFadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .panel-anim, .panel-exit, .login-page-enter {
      animation: none !important;
      opacity: 1 !important;
      transform: none !important;
    }
    .bg-exit { animation: none !important; }
  }
  .panel-exit {
    animation: panelOut var(--exit-dur) var(--exit-ease) both !important;
  }
  .bg-exit {
    animation: bgFadeOut var(--exit-dur) var(--exit-ease) both !important;
  }
  .login-page-enter {
    animation: bgFadeIn var(--panel-dur) var(--panel-ease) both;
  }
  .login-label {
    font-weight: 300;
    font-size: 0.8125rem;
    color: rgb(161 161 170);
    letter-spacing: 0.01em;
  }
`;

const MFA_DIGIT_COUNT = 6;

const MainLogin = ({ fromLogout }) => {
  const navigate = useNavigate();
  const { setIsUserLoggedIn } = useAuth();
  const { refreshUser } = useUserContext();
  const { setDarkmode, setDarkModeLoading } = useTheme();

  const [showMfa, setShowMfa] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [jwtToken, setJwtToken] = useState("");
  const [values, setValues] = useState(new Array(MFA_DIGIT_COUNT).fill(""));
  const [credential, setCredential] = useState(DEFAULT_CREDENTIALS);
  const [exiting, setExiting] = useState(false);

  const inputsRef = useRef([]);
  const panelRef = useRef(null);

  const handlePinChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);
    if (value.length === 1 && index < MFA_DIGIT_COUNT - 1)
      inputsRef.current[index + 1]?.focus();
    if (index === MFA_DIGIT_COUNT - 1 && value.length === 1)
      userVerify(newValues);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && e.target.value === "" && index > 0) {
      const newValues = [...values];
      newValues[index - 1] = "";
      setValues(newValues);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredential((prev) => ({ ...prev, [name]: value }));
  };

  const getTheme = useCallback(() => {
    getLightDarkMode()
      .then((response) => {
        const isDark = response.data.dark_mode === true;
        document.documentElement.classList.toggle("dark", isDark);
        setDarkmode(isDark);
        setDarkModeLoading(false);
      })
      .catch((e) => console.error("Error fetching theme:", e));
  }, [setDarkmode, setDarkModeLoading]);

  const handleLoginSuccess = useCallback(
    (token) => {
      localStorage.setItem("user", token);
      getTheme();
      // Populate the user identity now that the token is stored — the
      // AuthProvider's initial getUserFirstLast ran pre-login and got nothing.
      refreshUser().catch((e) => console.error("Error fetching user:", e));
      setExiting(true);
      setTimeout(() => {
        setIsUserLoggedIn(true);
        navigate("/home");
      }, 400);
    },
    [getTheme, navigate, setIsUserLoggedIn, refreshUser],
  );

  const userVerify = useCallback(
    (enteredValues) => {
      setErrorMessage("");
      validatePin(jwtToken, enteredValues.join(""))
        .then((response) => {
          const msg = response.data.message;
          if (msg === "success") {
            handleLoginSuccess(jwtToken);
          } else if (msg === "fail timeout") {
            setErrorMessage("Your pin has expired");
          } else if (msg?.includes("wrong pin")) {
            setErrorMessage("Your pin is wrong");
          } else {
            setErrorMessage("Contact your admin");
          }
        })
        .catch((e) => console.error("Error verifying pin:", e));
    },
    [handleLoginSuccess, jwtToken],
  );

  const userLogin = useCallback(() => {
    login(credential.email, credential.password)
      .then((response) => {
        const { accessToken, status, mfa } = response.data;
        if (accessToken && status !== 501) {
          if (mfa === true) {
            setJwtToken(accessToken);
            createPin(accessToken);
            setShowMfa(true);
          } else {
            handleLoginSuccess(accessToken);
          }
        } else {
          alert("Please enter correct email or password");
        }
      })
      .catch((error) => {
        if (error.message === "Request failed with status code 501") {
          localStorage.setItem("user", "");
          setIsUserLoggedIn(false);
        } else {
          console.error("Error logging in:", error);
        }
      });
  }, [
    credential.email,
    credential.password,
    handleLoginSuccess,
    setIsUserLoggedIn,
  ]);

  useEffect(() => {
    const handleEnterPress = (e) => {
      if (e.key === "Enter" && !showMfa && !exiting) userLogin();
    };
    window.addEventListener("keydown", handleEnterPress);
    return () => window.removeEventListener("keydown", handleEnterPress);
  }, [showMfa, exiting, userLogin]);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const handleAnimEnd = () => {
      setTimeout(() => {
        if (el) el.style.willChange = "auto";
      }, 120);
    };
    el.addEventListener("animationend", handleAnimEnd);
    return () => el.removeEventListener("animationend", handleAnimEnd);
  }, []);

  return (
    <div
      className={`relative min-h-screen overflow-hidden bg-[#0b0f14] font-poppins font-light text-zinc-200 ${
        exiting ? "bg-exit" : fromLogout ? "login-page-enter" : ""
      }`}
    >
      <style>{ANIMATION_STYLE}</style>

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-[#1c9dea]/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[26rem] w-[26rem] rounded-full bg-[#1c9dea]/10 blur-3xl" />
      </div>

      <main className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col items-center justify-center px-6 py-12 sm:px-8">
        <div
          ref={panelRef}
          style={{ willChange: "transform, opacity" }}
          className={`transform-gpu w-full max-w-md ${
            exiting
              ? "panel-exit"
              : "panel-anim motion-safe:[animation:panelIn_var(--panel-dur)_var(--panel-ease)_both_var(--panel-delay)]"
          }`}
        >
          <div className="group relative">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-6 -z-10 rounded-3xl bg-[radial-gradient(ellipse_at_center,rgba(28,157,234,0.22),transparent_60%)] opacity-70 blur-2xl transition-opacity duration-300 group-focus-within:opacity-100"
            />

            <div className="rounded-lg border border-[#1f2733] bg-[#0f141a]/80 shadow-[0_12px_50px_rgba(0,0,0,0.55)] ring-1 ring-white/5 backdrop-blur-md transition-shadow duration-300 group-focus-within:shadow-[0_24px_80px_rgba(28,157,234,0.25)]">
              <div className="flex justify-center px-6 py-6">
                <img src={entitylogo} alt="" />
              </div>

              <div className="px-6 pb-6">
                {showMfa ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between rounded-lg bg-black/30 px-3 py-2">
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <span
                          className="inline-flex h-2 w-2 rounded-full bg-emerald-400/80"
                          aria-hidden
                        />
                        MFA active
                      </div>
                      <CountdownTimer initialSeconds={600} />
                    </div>

                    <p className="login-label">
                      Check email for Multi Factor Auth"
                    </p>

                    <div className="flex justify-between gap-2">
                      {values.map((value, index) => (
                        <input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          aria-label={`Digit ${index + 1}`}
                          maxLength="1"
                          value={value}
                          onChange={(e) => handlePinChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          ref={(el) => (inputsRef.current[index] = el)}
                          className="h-12 w-12 rounded-lg border border-white/10 bg-black/40 text-center text-2xl text-zinc-100 outline-none ring-velBlue/60 transition focus:ring-2"
                        />
                      ))}
                    </div>

                    {errorMessage && (
                      <p className="text-center text-sm text-red-400/90">
                        {errorMessage}
                      </p>
                    )}

                    <Button onClick={() => userVerify(values)}>Verify</Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <Input
                      label="Email Address"
                      labelClassName="login-label"
                      value={credential.email}
                      onChange={handleChange}
                      name="email"
                    />
                    <Input
                      label="Password"
                      labelClassName="login-label"
                      type="password"
                      value={credential.password}
                      onChange={handleChange}
                      name="password"
                    />
                    <Button onClick={userLogin} className="w-full">
                      Sign In
                    </Button>
                    <p className="text-center text-xs text-zinc-500">
                      Press ↵ Enter to submit
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainLogin;
